import { Heading, Link, Text } from "@smooth/design-system-components"
import { dunningCaseHref, pointTimeLabel } from "../flows"
import type { FlowCustomer } from "../flows"
import type { EvaluationCase } from "../types"

type Props = {
  /** 一覧を読み込み済みか。未取得の間は空表示を出さない */
  loaded: boolean
  customers: FlowCustomer[]
  /** 取り込み元のユーザー ID ごとのお客様の名前 */
  sourceUsers: Record<string, string | null>
  /** 開いている時点を含む流れの起点 */
  selectedRootId: number | null
  /** 開いている時点（ケース） */
  selectedCaseId?: number | null
  disabled: boolean
  onChoose: (record: EvaluationCase) => void
}

/**
 * お客様の見出し（名前・ユーザー ID・督促ケースへのリンク）。取り込み元の督促ケースが無いお客様は label（無ければ出さない）
 */
export function CustomerName({
  customer,
  sourceUsers,
}: {
  customer: Pick<FlowCustomer, "sourceDunningCaseId" | "sourceUserId"> & { label?: string | null }
  sourceUsers: Record<string, string | null>
}) {
  const { sourceDunningCaseId, sourceUserId } = customer
  if (!sourceDunningCaseId) return customer.label ? <>{customer.label}</> : null
  const name = sourceUserId ? sourceUsers[String(sourceUserId)] : null
  return (
    <span className="inline-flex flex-wrap items-baseline gap-x-2">
      <span>{name || (sourceUserId ? `ユーザー #${sourceUserId}` : "お客様")}</span>
      <span className="text-caption font-normal text-text-muted">
        {sourceUserId ? `ユーザー #${sourceUserId} · ` : ""}
        <Link href={dunningCaseHref(sourceDunningCaseId)} external>
          督促ケース #{sourceDunningCaseId}
        </Link>
      </span>
    </span>
  )
}

/**
 * お客様の流れの一覧。お客様ごとに流れ（起点のケース）を並べ、時点の数と最後の時点の日時を添える。
 * 一致率などのプロンプトの比較の指標は出さない（時点ごとの期待と AI の出力は右のタイムラインで見る）
 */
export function FlowList({
  loaded,
  customers,
  sourceUsers,
  selectedRootId,
  selectedCaseId,
  disabled,
  onChoose,
}: Props) {
  return (
    <aside
      aria-label="お客様と流れ"
      className="flex min-w-0 flex-col gap-4 border-b border-border-default py-5 min-[900px]:border-b-0 min-[900px]:border-r min-[900px]:pr-4"
    >
      <div className="max-h-[320px] overflow-y-auto min-[900px]:max-h-[72vh]">
        {customers.map((customer) => (
          // お客様ごとの枠に、時点（1時点目・2時点目…）をぶら下げる。枝分かれは字下げで出す
          <section key={customer.key} aria-label={`お客様 ${customer.key}`} className="pb-3">
            <div className="sticky top-0 bg-bg-default pb-1.5 pt-3.5">
              <Heading level={3}>
                <CustomerName customer={customer} sourceUsers={sourceUsers} />
              </Heading>
            </div>
            {customer.flows.flatMap((flow) =>
              flow.points.map((point) => {
                const selected =
                  selectedCaseId != null
                    ? selectedCaseId === point.record.id
                    : selectedRootId === flow.root.id && point.depth === 0
                return (
                  <button
                    key={point.record.id}
                    type="button"
                    aria-current={selected || undefined}
                    disabled={disabled}
                    aria-label={`${point.number}時点目 ${point.record.title}`}
                    onClick={() => onChoose(point.record)}
                    style={{ paddingLeft: `${0.625 + point.depth * 1.25}rem` }}
                    className={[
                      "flex w-full items-baseline gap-2 border-b border-l-[3px] border-b-border-default py-2 pr-2.5 text-left text-body text-text-default",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring disabled:cursor-not-allowed",
                      selected
                        ? "border-l-action-primary bg-bg-info font-bold"
                        : "border-l-transparent hover:bg-bg-muted",
                    ].join(" ")}
                  >
                    <span className="shrink-0">{point.number}時点目</span>
                    <span className="text-caption font-normal text-text-muted">
                      {pointTimeLabel(point.record.scenario.asOf)}
                    </span>
                  </button>
                )
              })
            )}
          </section>
        ))}
        {loaded && customers.length === 0 && (
          <Text>
            まだ流れがありません。「お客様の会話を読み込む」から督促ケース・ユーザー ID
            を読み込むか、空から作ってください。
          </Text>
        )}
      </div>
    </aside>
  )
}
